import React, { useState } from 'react';
import { useMaestroContext, VisaoMaestro } from '../context/MaestroContext';
import { isMapaResultados, isMotorConciliacao, isRevisorContabil, isWorkflowSox } from '../models/produtos';
import { Atividade } from '../models/atividade';
import { diasUteisEntre } from '../utils/businessDays';
import { empresaEstaEmRisco } from '../utils/empresaStatus';
import { formatarData } from '../utils/formatters';
import { LABEL_STATUS_CICLO } from '../utils/labels';
import AtividadeDrawer from '../components/AtividadeDrawer';
import Card from '../components/Card';
import EstadoErro from '../components/EstadoErro';
import EstadoVazio from '../components/EstadoVazio';
import Header from '../components/Header';
import IndicadorCard, { IndicadorCardProps } from '../components/IndicadorCard';
import MetricsGrid from '../components/MetricsGrid';
import ModalCriarEscopo from '../components/ModalCriarEscopo';
import { SkeletonCard, SkeletonTabela } from '../components/Skeletons';
import SaudeFechamento from '../components/SaudeFechamento';
import SeletorEscopo from '../components/SeletorEscopo';
import WizardAberturaCiclo from '../components/WizardAberturaCiclo';
import WizardEncerramentoCiclo from '../components/WizardEncerramentoCiclo';
import WizardReaberturaCiclo from '../components/WizardReaberturaCiclo';
import AtividadeTreeTable from '../components/views/AtividadeTreeTable';
import VisaoCaminhoCritico from '../components/views/VisaoCaminhoCritico';
import VisaoCalendario from '../components/views/VisaoCalendario';
import VisaoDelegacao from '../components/views/VisaoDelegacao';
import VisaoDependencias from '../components/views/VisaoDependencias';
import VisaoExecutiva from '../components/views/VisaoExecutiva';
import VisaoGantt from '../components/views/VisaoGantt';
import VisaoLinhaDoTempo from '../components/views/VisaoLinhaDoTempo';
import VisaoPDCA from '../components/views/VisaoPDCA';
import VisaoRanking from '../components/views/VisaoRanking';
import VisaoRiscos from '../components/views/VisaoRiscos';

const VISOES: Array<{ id: VisaoMaestro; titulo: string; icone: string }> = [
  { id: 'executiva', titulo: 'Executiva', icone: 'bi bi-speedometer2' },
  { id: 'calendario', titulo: 'Calendário', icone: 'bi bi-calendar3' },
  { id: 'linha-do-tempo', titulo: 'Linha do Tempo', icone: 'bi bi-signpost-split' },
  { id: 'gantt', titulo: 'Gantt', icone: 'bi bi-bar-chart-steps' },
  { id: 'hierarquia', titulo: 'Lista Hierárquica', icone: 'bi bi-list-nested' },
  { id: 'area', titulo: 'Por Área', icone: 'bi bi-diagram-3' },
  { id: 'responsavel', titulo: 'Por Responsável', icone: 'bi bi-person-badge' },
  { id: 'empresa', titulo: 'Por Empresa', icone: 'bi bi-building' },
  { id: 'dependencias', titulo: 'Dependências', icone: 'bi bi-hourglass-split' },
  { id: 'caminho-critico', titulo: 'Caminho Crítico', icone: 'bi bi-lightning-charge' },
  { id: 'pdca', titulo: 'PDCA', icone: 'bi bi-clipboard-pulse' },
  { id: 'ranking', titulo: 'Ranking', icone: 'bi bi-trophy' },
  { id: 'riscos', titulo: 'Riscos', icone: 'bi bi-shield-exclamation' },
  { id: 'delegacao', titulo: 'Delegação', icone: 'bi bi-arrow-left-right' },
];

// KPIs cujo clique leva a uma das visões baseadas em lista de atividades (hierarquia/área/
// responsável/empresa/dependências) — para esses, além de navegar, filtramos a lista pela
// condição do próprio indicador, para o clique realmente "levar ao dado" em vez de só trocar de
// aba e deixar o usuário procurar manualmente entre todas as atividades do ciclo.
const PREDICADOS_KPI: Record<string, (a: Atividade) => boolean> = {
  concluidas: a => a.status === 'CONCLUIDA',
  andamento: a => a.status === 'EM_ANDAMENTO',
  risco: a => a.criticidade === 'CRITICA' && a.status !== 'CONCLUIDA',
  atrasadas: a => a.status === 'ATRASADA',
  'aguardando-evidencia': a => a.status === 'AGUARDANDO_EVIDENCIA',
  'entregas-pendentes': a => a.status === 'NAO_INICIADA' || a.status === 'EM_ANDAMENTO',
  aprovacoes: a => a.status === 'EM_REVISAO',
};

const MaestroCockpitPage: React.FC = () => {
  const {
    carregando, erro, semDadosCadastrados, recarregar,
    cicloAtivo, ciclosAnteriores, setCicloAtivoId, empresasEscopo, indicadores, atividades, dependencias, produtosIntegrados,
    acoesPDCA, pontuacoes, feriados, visaoAtiva, setVisaoAtiva, abrirAtividade,
  } = useMaestroContext();

  const [abrindoCiclo, setAbrindoCiclo] = useState(false);
  const [encerrandoCiclo, setEncerrandoCiclo] = useState(false);
  const [reabrindoCiclo, setReabrindoCiclo] = useState(false);
  const [criandoEscopo, setCriandoEscopo] = useState(false);
  const [filtroKpi, setFiltroKpi] = useState<{ id: string; titulo: string } | null>(null);

  // Os modais/wizards são montados uma única vez, fora destes ramos condicionais, e sempre no
  // final deste componente — se ficassem dentro de um `return` por ramo (como antes), um
  // `recarregar()` disparado por uma ação dentro de um modal aberto (ex.: publicar um calendário)
  // podia fazer a página passar pelo estado "carregando" e trocar de ramo, desmontando o modal/
  // wizard e reiniciando todo o estado dele (ex.: voltar para a etapa 1 do wizard sem motivo).
  let corpo: React.ReactElement;

  if (carregando) {
    corpo = (
      <div className='mc-page'>
        <Header title='Maestro de Fechamento Contábil' subtitle='Carregando dados do cockpit...' />
        <SkeletonCard />
        <SkeletonTabela linhas={8} />
      </div>
    );
  } else if (erro) {
    corpo = (
      <div className='mc-page'>
        <Header title='Maestro de Fechamento Contábil' />
        <Card><EstadoErro titulo='Não foi possível carregar o Maestro Contábil' descricao={erro} onTentarNovamente={recarregar} /></Card>
      </div>
    );
  } else if (semDadosCadastrados) {
    corpo = (
      <div className='mc-page'>
        <Header title='Maestro de Fechamento Contábil' />
        <Card>
          <EstadoVazio
            icone='bi bi-diagram-3'
            titulo='Nenhum grupo econômico cadastrado ainda'
            descricao='Cadastre ao menos um Grupo Econômico e uma Empresa vinculada para começar a usar o Cockpit.'
            acao={<button type='button' className='mc-btn mc-btn--primary' onClick={() => setCriandoEscopo(true)}>Cadastrar grupo econômico</button>}
          />
        </Card>
      </div>
    );
  } else if (!cicloAtivo.id) {
    corpo = (
      <div className='mc-page'>
        <Header title='Maestro de Fechamento Contábil' actions={<button type='button' className='mc-btn mc-btn--primary' onClick={() => setAbrindoCiclo(true)}>Abrir novo ciclo</button>} />
        <Card>
          <EstadoVazio icone='bi bi-calendar3' titulo='Nenhum ciclo de fechamento aberto' descricao='Abra o primeiro ciclo de fechamento para começar a acompanhar as atividades.' />
        </Card>
      </div>
    );
  } else {
    corpo = renderCockpitCompleto();
  }

  return (
    <>
      {corpo}
      <ModalCriarEscopo aberto={criandoEscopo} onFechar={() => setCriandoEscopo(false)} />
      <WizardAberturaCiclo aberto={abrindoCiclo} onFechar={() => setAbrindoCiclo(false)} />
      <WizardEncerramentoCiclo aberto={encerrandoCiclo} onFechar={() => setEncerrandoCiclo(false)} />
      <WizardReaberturaCiclo aberto={reabrindoCiclo} onFechar={() => setReabrindoCiclo(false)} />
    </>
  );

  function renderCockpitCompleto(): React.ReactElement {

  const empresasEmRisco = empresasEscopo.filter(e => empresaEstaEmRisco(e, atividades, cicloAtivo.progressoEsperado)).length;
  const areasContribuiram = pontuacoes.filter(p => p.classificacao === 'EXCELENCIA' || p.classificacao === 'ALTO_DESEMPENHO').length;
  const areasAtencao = pontuacoes.filter(p => p.classificacao === 'REQUER_ATENCAO').length;
  const pdcaAbertas = acoesPDCA.filter(a => a.status === 'ABERTA' || a.status === 'EM_ANDAMENTO').length;
  const emRevisao = atividades.filter(a => a.status === 'EM_REVISAO').length;

  const motor = produtosIntegrados.filter(isMotorConciliacao);
  const mapa = produtosIntegrados.filter(isMapaResultados);
  const revisor = produtosIntegrados.filter(isRevisorContabil);
  const sox = produtosIntegrados.filter(isWorkflowSox);

  const irPara = (visao: VisaoMaestro, kpiId?: string, kpiTitulo?: string) => {
    setVisaoAtiva(visao);
    setFiltroKpi(kpiId && PREDICADOS_KPI[kpiId] ? { id: kpiId, titulo: kpiTitulo ?? '' } : null);
  };

  const atividadesFiltradas = filtroKpi ? atividades.filter(PREDICADOS_KPI[filtroKpi.id]) : atividades;

  const totalAtividades = indicadores.totalAtividades;
  const diasUteisTotalCiclo = diasUteisEntre(cicloAtivo.dataAbertura, cicloAtivo.previsaoConclusao, feriados);
  // CicloFechamento.diasUteisRestantes vem sempre 0 do adapter (o backend não devolve esse campo
  // pronto) — calculado aqui com a mesma função usada para o total, de hoje até a previsão de
  // conclusão, em vez de deixar o card "Dias úteis restantes" sempre zerado.
  const hojeIso = (() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  })();
  const diasUteisRestantes = cicloAtivo.previsaoConclusao ? diasUteisEntre(hojeIso, cicloAtivo.previsaoConclusao, feriados) : 0;
  const atividadesEmRisco = atividades.filter(a => a.criticidade === 'CRITICA' && a.status !== 'CONCLUIDA').length;
  const itensSoxAlertaSoma = sox.reduce((s, p) => s + p.emAlerta, 0);
  const itensSoxFilaSoma = sox.reduce((s, p) => s + p.itensNaFila, 0);
  const pendenciasMotorSoma = motor.reduce((s, p) => s + p.contasPendentes, 0);
  const contasTotaisMotorSoma = motor.reduce((s, p) => s + p.contasTotais, 0);
  const divergenciasMapaSoma = mapa.reduce((s, p) => s + p.contasComDivergencia, 0);
  const contasTotaisMapaSoma = mapa.reduce((s, p) => s + p.contasTotais, 0);
  const errosRevisorSoma = revisor.reduce((s, p) => s + p.erros, 0);
  const modulosAvaliadosRevisorSoma = revisor.reduce((s, p) => s + p.modulosAvaliados, 0);

  const indicadoresCards: IndicadorCardProps[] = [
    { id: 'prog-esperado', titulo: 'Progresso esperado', valor: `${cicloAtivo.progressoEsperado}%`, cor: 'neutro', onClick: () => irPara('executiva'), gauge: { valor: cicloAtivo.progressoEsperado, maximo: 100, sufixo: '%' } },
    { id: 'prog-realizado', titulo: 'Progresso realizado', valor: `${cicloAtivo.progressoRealizado}%`, cor: 'primary', onClick: () => irPara('executiva'), gauge: { valor: cicloAtivo.progressoRealizado, maximo: 100, sufixo: '%' } },
    { id: 'desvio', titulo: 'Desvio do planejamento', valor: `${cicloAtivo.desvio > 0 ? '+' : ''}${cicloAtivo.desvio}%`, cor: cicloAtivo.desvio < 0 ? 'danger' : 'success', onClick: () => irPara('executiva'), gauge: { valor: cicloAtivo.desvio, maximo: 100, sufixo: '%' } },
    { id: 'previsao', titulo: 'Previsão de conclusão', valor: formatarData(cicloAtivo.previsaoConclusao), cor: 'info', onClick: () => irPara('calendario') },
    { id: 'dias-uteis', titulo: 'Dias úteis restantes', valor: diasUteisRestantes, cor: 'info', onClick: () => irPara('calendario'), gauge: { valor: diasUteisRestantes, maximo: diasUteisTotalCiclo } },
    { id: 'concluidas', titulo: 'Atividades concluídas', valor: indicadores.concluidas, cor: 'success', onClick: () => irPara('hierarquia', 'concluidas', 'Atividades concluídas'), gauge: { valor: indicadores.concluidas, maximo: totalAtividades } },
    { id: 'andamento', titulo: 'Atividades em andamento', valor: indicadores.emAndamento, cor: 'info', onClick: () => irPara('hierarquia', 'andamento', 'Atividades em andamento'), gauge: { valor: indicadores.emAndamento, maximo: totalAtividades } },
    { id: 'risco', titulo: 'Atividades em risco', valor: atividadesEmRisco, cor: 'danger', onClick: () => irPara('hierarquia', 'risco', 'Atividades em risco'), gauge: { valor: atividadesEmRisco, maximo: totalAtividades } },
    { id: 'atrasadas', titulo: 'Atividades atrasadas', valor: indicadores.atrasadas, cor: 'danger', onClick: () => irPara('hierarquia', 'atrasadas', 'Atividades atrasadas'), gauge: { valor: indicadores.atrasadas, maximo: totalAtividades } },
    { id: 'aguardando-dep', titulo: 'Aguardando dependência', valor: indicadores.aguardandoDependencia, cor: 'warning', onClick: () => irPara('dependencias'), gauge: { valor: indicadores.aguardandoDependencia, maximo: totalAtividades } },
    { id: 'aguardando-evidencia', titulo: 'Aguardando evidência', valor: indicadores.aguardandoEvidencia, cor: 'warning', onClick: () => irPara('hierarquia', 'aguardando-evidencia', 'Aguardando evidência'), gauge: { valor: indicadores.aguardandoEvidencia, maximo: totalAtividades } },
    { id: 'entregas-pendentes', titulo: 'Entregas pendentes', valor: indicadores.naoIniciadas + indicadores.emAndamento, cor: 'warning', onClick: () => irPara('hierarquia', 'entregas-pendentes', 'Entregas pendentes'), gauge: { valor: indicadores.naoIniciadas + indicadores.emAndamento, maximo: totalAtividades } },
    { id: 'aprovacoes', titulo: 'Aprovações pendentes', valor: emRevisao, cor: 'warning', onClick: () => irPara('hierarquia', 'aprovacoes', 'Aprovações pendentes'), gauge: { valor: emRevisao, maximo: totalAtividades } },
    { id: 'sox-alerta', titulo: 'Itens SOX em alerta de prazo', valor: itensSoxAlertaSoma, cor: 'warning', onClick: () => irPara('executiva'), gauge: { valor: itensSoxAlertaSoma, maximo: Math.max(itensSoxFilaSoma, itensSoxAlertaSoma) } },
    { id: 'empresas-risco', titulo: 'Empresas em risco', valor: empresasEmRisco, cor: 'danger', onClick: () => irPara('empresa'), gauge: { valor: empresasEmRisco, maximo: empresasEscopo.length } },
    { id: 'areas-contribuiram', titulo: 'Responsáveis que mais contribuíram', valor: areasContribuiram, cor: 'success', onClick: () => irPara('ranking'), gauge: { valor: areasContribuiram, maximo: pontuacoes.length } },
    { id: 'areas-atencao', titulo: 'Responsáveis que precisam de atenção', valor: areasAtencao, cor: 'danger', onClick: () => irPara('ranking'), gauge: { valor: areasAtencao, maximo: pontuacoes.length } },
    { id: 'pdca-abertas', titulo: 'Ações de PDCA abertas', valor: pdcaAbertas, cor: 'info', onClick: () => irPara('pdca'), gauge: { valor: pdcaAbertas, maximo: acoesPDCA.length } },
    { id: 'motor', titulo: 'Pendências do Motor de Conciliação', valor: pendenciasMotorSoma, cor: 'primary', onClick: () => irPara('executiva'), gauge: { valor: pendenciasMotorSoma, maximo: Math.max(contasTotaisMotorSoma, pendenciasMotorSoma) } },
    { id: 'mapa', titulo: 'Divergências do Mapa de Resultado', valor: divergenciasMapaSoma, cor: 'danger', onClick: () => irPara('executiva'), gauge: { valor: divergenciasMapaSoma, maximo: Math.max(contasTotaisMapaSoma, divergenciasMapaSoma) } },
    { id: 'revisor', titulo: 'Erros do Revisor Contábil', valor: errosRevisorSoma, cor: 'dark', onClick: () => irPara('executiva'), gauge: { valor: errosRevisorSoma, maximo: Math.max(modulosAvaliadosRevisorSoma, errosRevisorSoma) } },
    { id: 'sox', titulo: 'Itens na fila do Workflow SOX', valor: itensSoxFilaSoma, cor: 'dark', onClick: () => irPara('executiva'), gauge: { valor: itensSoxFilaSoma, maximo: Math.max(itensSoxFilaSoma, 1) } },
  ];

  return (
    <div className='mc-page'>
      <Header
        title='Maestro de Fechamento Contábil'
        subtitle={`Competência ${cicloAtivo.competencia} · ${LABEL_STATUS_CICLO[cicloAtivo.status]} · Prazo oficial ${formatarData(cicloAtivo.prazoOficial)}`}
        actions={<>
          {ciclosAnteriores.length > 0 && (
            <select
              className='mc-btn mc-btn--secondary'
              value={cicloAtivo.id}
              onChange={e => setCicloAtivoId(Number(e.target.value))}
              title='Trocar o ciclo de fechamento em exibição'
            >
              <option value={cicloAtivo.id}>{cicloAtivo.competencia} · {LABEL_STATUS_CICLO[cicloAtivo.status]}</option>
              {ciclosAnteriores.map(c => (
                <option key={c.id} value={c.id}>{c.competencia} · {LABEL_STATUS_CICLO[c.status]}</option>
              ))}
            </select>
          )}
          <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setAbrindoCiclo(true)}>Abrir novo ciclo</button>
          {cicloAtivo.status === 'ENCERRADO'
            ? <button type='button' className='mc-btn mc-btn--primary' onClick={() => setReabrindoCiclo(true)}>Reabrir ciclo</button>
            : <button type='button' className='mc-btn mc-btn--primary' onClick={() => setEncerrandoCiclo(true)}>Encerrar ciclo</button>}
        </>}
      />

      <Card>
        <SeletorEscopo />
      </Card>

      <MetricsGrid itens={indicadoresCards} />

      <SaudeFechamento />

      <Card>
        <div className='mc-view-switcher'>
          {VISOES.map(v => (
            <button key={v.id} type='button' className={visaoAtiva === v.id ? 'is-active' : ''} onClick={() => { setVisaoAtiva(v.id); setFiltroKpi(null); }}>
              <i className={v.icone} /> {v.titulo}
            </button>
          ))}
        </div>

        {filtroKpi && (
          <div className='mc-filtro-kpi'>
            <span><i className='bi bi-funnel-fill' /> Filtrando por: <strong>{filtroKpi.titulo}</strong> ({atividadesFiltradas.length} de {atividades.length} atividades)</span>
            <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setFiltroKpi(null)}>Limpar filtro</button>
          </div>
        )}

        <div className='mc-view-content'>
          {visaoAtiva === 'executiva' && <VisaoExecutiva onAbrirAtividade={abrirAtividade} />}
          {visaoAtiva === 'calendario' && <VisaoCalendario />}
          {visaoAtiva === 'linha-do-tempo' && <VisaoLinhaDoTempo atividades={atividades} onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'gantt' && <VisaoGantt atividades={atividades} dependencias={dependencias} onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'hierarquia' && <AtividadeTreeTable atividades={atividadesFiltradas} dependencias={dependencias} agrupamento='hierarquia' onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'area' && <AtividadeTreeTable atividades={atividadesFiltradas} dependencias={dependencias} agrupamento='area' onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'responsavel' && <AtividadeTreeTable atividades={atividadesFiltradas} dependencias={dependencias} agrupamento='responsavel' onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'empresa' && <AtividadeTreeTable atividades={atividadesFiltradas} dependencias={dependencias} agrupamento='empresa' onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'dependencias' && <VisaoDependencias atividades={atividades} dependencias={dependencias} onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'caminho-critico' && <VisaoCaminhoCritico atividades={atividades} dependencias={dependencias} onSelecionar={abrirAtividade} />}
          {visaoAtiva === 'pdca' && <VisaoPDCA />}
          {visaoAtiva === 'ranking' && <VisaoRanking />}
          {visaoAtiva === 'riscos' && <VisaoRiscos />}
          {visaoAtiva === 'delegacao' && <VisaoDelegacao />}
        </div>
      </Card>

      <AtividadeDrawer />
    </div>
    );
  }
};

export default MaestroCockpitPage;
